import {
  Box,
  Button,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarQuickFilter,
  GridCsvExportMenuItem,
  GridPrintExportMenuItem,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { tokens } from "../dashboard/theme";
import NavigationHeader from "../components/NavigationHeader";
import config from "./global/config";
import { usePermissions } from "./hooks/usePermissions";
import {
  AddAchatModal,
  // EditAchatModal,
  DeleteAchatModal,
} from "./AchatModals";
import AchatCashPaymentModal from "./AchatCashPaymentModal";
import PaymentIcon from "@mui/icons-material/Payment";
import AchatInstallmentPaymentModal from "./AchatInstallmentPaymentModal";
import AchatDetailsGrid from "./AchatDetailsGrid";
import AchatPaymentDetailsGrid from "./AchatPaymentDetailsGrid";
import AchatFilter from "./AchatFilter";
import { format, startOfDay, endOfDay } from "date-fns";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

function AchatManagement() {
  const { hasPermission } = usePermissions();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [activeTab, setActiveTab] = useState(0);
  const [purchases, setPurchases] = useState([]);
  const [purchaseDetails, setPurchaseDetails] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [modalAdd, setModalAdd] = useState(false);
  // const [modalEdit, setModalEdit] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [installmentModalOpen, setInstallmentModalOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const today = new Date();
  const [filters, setFilters] = useState({
    id_fournisseur: "",
    date_debut: startOfDay(today),
    date_fin: endOfDay(today),
    statut_paiement: "",
  });
  const [existingPayments, setExistingPayments] = useState({});

  // Add this new function to check for existing payments
  const checkExistingPayments = async (id_achat) => {
    try {
      // Run both checks in parallel
      const [cashResponse, installmentResponse] = await Promise.all([
        api.get(`paiements_fournisseurs_cash.php?id_achat=${id_achat}`),
        api.get(`paiements_fournisseurs_tranches.php?id_achat=${id_achat}`),
      ]);

      const hasCashPayment =
        Array.isArray(cashResponse.data) &&
        cashResponse.data.length > 0 &&
        cashResponse.data.some((payment) => payment.id_achat === id_achat);

      const hasInstallmentPayment =
        Array.isArray(installmentResponse.data) &&
        installmentResponse.data.length > 0 &&
        installmentResponse.data.some(
          (payment) => payment.id_achat === id_achat
        );

      return {
        exists: hasCashPayment || hasInstallmentPayment,
        type: hasCashPayment
          ? "cash"
          : hasInstallmentPayment
            ? "installment"
            : null,
        cashPayment: hasCashPayment,
        installmentPayment: hasInstallmentPayment,
      };
    } catch (error) {
      console.error("Error checking existing payments:", error);
      return {
        exists: false,
        type: null,
        cashPayment: false,
        installmentPayment: false,
      };
    }
  };
  const api = axios.create({
    baseURL: config.apiUrl,
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchPurchasesWithFilters(newFilters);
  };

  const ensureArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return [data];
  };

  const fetchPurchasesWithFilters = async (currentFilters = filters) => {
    try {
      let queryParams = new URLSearchParams();

      if (currentFilters.id_fournisseur) {
        queryParams.append("id_fournisseur", currentFilters.id_fournisseur);
      }
      if (currentFilters.date_debut) {
        queryParams.append(
          "date_debut",
          format(currentFilters.date_debut, "yyyy-MM-dd")
        );
      }
      if (currentFilters.date_fin) {
        queryParams.append(
          "date_fin",
          format(currentFilters.date_fin, "yyyy-MM-dd")
        );
      }
      if (currentFilters.statut_paiement) {
        queryParams.append("statut_paiement", currentFilters.statut_paiement);
      }

      const response = await api.get(`achats.php?${queryParams.toString()}`);
      const purchasesData = ensureArray(response.data);

      // Check existing payments for each purchase
      const paymentsCheck = {};
      await Promise.all(
        purchasesData.map(async (purchase) => {
          try {
            const paymentStatus = await checkExistingPayments(
              purchase.id_achat
            );
            paymentsCheck[purchase.id_achat] = paymentStatus;
          } catch (error) {
            console.error(
              `Error checking payments for purchase ${purchase.id_achat}:`,
              error
            );
            paymentsCheck[purchase.id_achat] = {
              exists: false,
              type: null,
              cashPayment: false,
              installmentPayment: false,
            };
          }
        })
      );

      setExistingPayments(paymentsCheck);
      setPurchases(purchasesData);
    } catch (error) {
      console.error("Error fetching purchases:", error);
      setError(error.message);
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    }
  };

  const fetchPurchases = async (isToday = false) => {
    try {
      const response = await api.get(
        "achats.php" + isToday === true ? "?today=true" : ""
      );
      const purchasesData = ensureArray(response.data);

      const paymentsCheck = {};
      for (const purchase of purchasesData) {
        try {
          const paymentStatus = await checkExistingPayments(purchase.id_achat);
          paymentsCheck[purchase.id_achat] = paymentStatus;
        } catch (error) {
          console.error(
            `Error checking payments for purchase ${purchase.id_achat}:`,
            error
          );
          paymentsCheck[purchase.id_achat] = {
            exists: false,
            type: null,
            cashPayment: false,
            installmentPayment: false,
          };
        }
      }

      setExistingPayments(paymentsCheck);
      setPurchases(purchasesData);
    } catch (error) {
      console.error("Error fetching purchases:", error);
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    }
  };

  const handleAddPurchaseDetail = async (detailData) => {
    try {
      const formData = new FormData();
      formData.append("METHOD", "POST");
      formData.append("id_achat", detailData.id_achat);
      formData.append("id_produit", detailData.id_produit);
      formData.append("quantite", detailData.quantite);
      formData.append("prix_achat_unitaire", detailData.prix_achat_unitaire);
      formData.append("user_cre", "SYSTEM");

      await api.post("details_achats.php", formData);
      fetchPurchaseDetails(selectedPurchase.id_achat);
    } catch (error) {
      console.error("Error adding purchase detail:", error);
    }
  };

  const handleEditPurchaseDetail = async (id_detail_achat, detailData) => {
    try {
      const formData = new FormData();
      formData.append("METHOD", "PUT");
      formData.append("id_produit", detailData.id_produit);
      formData.append("quantite", detailData.quantite);
      formData.append("prix_achat_unitaire", detailData.prix_achat_unitaire);
      formData.append("user_upd", "SYSTEM");

      await api.post(
        `details_achats.php?id_detail_achat=${id_detail_achat}`,
        formData
      );
      fetchPurchaseDetails(selectedPurchase.id_achat);
    } catch (error) {
      console.error("Error updating purchase detail:", error);
    }
  };

  const handleDeletePurchaseDetail = async (id_detail_achat) => {
    try {
      const formData = new FormData();
      formData.append("METHOD", "DELETE");

      await api.post(
        `details_achats.php?id_detail_achat=${id_detail_achat}`,
        formData
      );
      fetchPurchaseDetails(selectedPurchase.id_achat);
    } catch (error) {
      console.error("Error deleting purchase detail:", error);
    }
  };
  const handlePaymentComplete = async () => {
    try {
      await fetchPurchases(true);

      if (selectedPurchase) {
        const response = await api.get(
          `achats.php?id_achat=${selectedPurchase.id_achat}`
        );
        const updatedPurchase = response.data[0];
        setSelectedPurchase(updatedPurchase);

        // if (activeTab === 2) {
        //   await fetchPaymentDetails(selectedPurchase.id_achat);
        // }
      }

      setPaymentModalOpen(false);
      setInstallmentModalOpen(false);
    } catch (error) {
      console.error("Error refreshing data after payment:", error);
    }
  };

  const validateNumericValue = (value, fallback = 0) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  };

  const handleAddPurchase = async (purchaseData) => {
    try {
      console.log("purchaseData: ", purchaseData);
      if (
        !purchaseData.id_fournisseur ||
        !purchaseData.type_paiement ||
        !purchaseData.date_achat
      ) {
        throw new Error(
          "Missing required fields: Fournisseur, Payment Type, and Date are required"
        );
      }

      // Ensure montant_total is a valid number
      const validatedTotal = validateNumericValue(
        purchaseData.montant_total,
        0
      );
      const formattedTotal = validatedTotal.toFixed(2);

      const purchaseFormData = new FormData();
      purchaseFormData.append("METHOD", "POST");
      purchaseFormData.append("id_fournisseur", purchaseData.id_fournisseur);
      purchaseFormData.append("categorie_code", "1");
      purchaseFormData.append("date_achat", purchaseData.date_achat);
      purchaseFormData.append("montant_total", formattedTotal);
      purchaseFormData.append("type_paiement", purchaseData.type_paiement);
      purchaseFormData.append(
        "statut_paiement",
        purchaseData.statut_paiement || "En attente"
      );
      purchaseFormData.append("commentaire", purchaseData.commentaire || "");
      purchaseFormData.append("user_cre", "SYSTEM");

      // console.log("purchaseFormData: ", purchaseFormData);

      // console.log("Purchase Data Before API Call:", purchaseData);
      const purchaseResponse = await api.post("achats.php", purchaseFormData);
      // console.log("API Response for Purchase:", purchaseResponse);

      if (
        !purchaseResponse.data ||
        typeof purchaseResponse.data.id_achat === "undefined"
      ) {
        console.error("Invalid purchase response:", purchaseResponse.data);
        throw new Error(
          purchaseResponse.data?.error || "Invalid response format from server"
        );
      }

      const purchaseId = purchaseResponse.data.id_achat;

      // Handle purchase details
      if (
        Array.isArray(purchaseData.details) &&
        purchaseData.details.length > 0
      ) {
        console.log("Purchase Details:", purchaseData.details);
        const detailPromises = purchaseData.details.map(async (detail) => {
          if (!detail.id_produit || !detail.quantite) {
            console.warn("Skipping invalid detail:", detail);
            return null;
          }

          const validatedPrice = validateNumericValue(
            detail.prix_achat_unitaire,
            0
          );
          const validatedQuantity = validateNumericValue(detail.quantite, 0);

          const detailFormData = new FormData();
          detailFormData.append("METHOD", "POST");
          detailFormData.append("id_achat", purchaseId);
          detailFormData.append("id_produit", detail.id_produit);
          detailFormData.append("quantite", validatedQuantity.toString());
          detailFormData.append(
            "prix_achat_unitaire",
            validatedPrice.toFixed(2)
          );
          detailFormData.append("user_cre", "SYSTEM");

          try {
            const detailResponse = await api.post(
              "details_achats.php",
              detailFormData
            );
            return detailResponse.data;
          } catch (error) {
            console.error("Error creating detail:", error);
            throw error;
          }
        });

        await Promise.all(detailPromises.filter(Boolean));
      }

      setModalAdd(false);
      await fetchPurchases(true);

      return {
        success: true,
        message: "Purchase created successfully",
        id: purchaseId,
      };
    } catch (error) {
      console.error("Error in purchase creation:", error);
      setError(
        error.message ||
          "An unexpected error occurred while creating the purchase"
      );

      return {
        success: false,
        message: error.message || "Failed to create purchase",
      };
    }
  };

  const getPaymentButtonProps = (purchase, paymentInfo) => {
    const isPaid = purchase.statut_paiement === "Payé";
    const hasExistingPayment = paymentInfo?.exists;

    // Button should be disabled only if:
    // 1. Purchase is already marked as fully paid
    // 2. OR there's a matching payment record for the purchase's payment type
    const isPaymentDisabled =
      isPaid ||
      (hasExistingPayment &&
        ((purchase.type_paiement === "Cash" && paymentInfo.type === "cash") ||
          (purchase.type_paiement === "Tranche" &&
            paymentInfo.type === "installment")));

    let label = "Process Payment";
    if (isPaid) {
      label = "Payment Completed";
    } else if (hasExistingPayment) {
      if (paymentInfo.type === "cash") {
        label = "Cash Payment Processed";
      } else if (paymentInfo.type === "installment") {
        label = "Installment Plan Created";
      }
    }

    return {
      icon: <PaymentIcon />,
      label,
      onClick: () => !isPaymentDisabled && handlePaymentClick(purchase),
      disabled: isPaymentDisabled,
      className: isPaymentDisabled ? "Mui-disabled" : "",
      showInMenu: false,
      sx: {
        opacity: isPaymentDisabled ? 0.38 : 1,
        cursor: isPaymentDisabled ? "not-allowed" : "pointer",
        "&.Mui-disabled": {
          opacity: 0.38,
          pointerEvents: "none",
        },
      },
    };
  };
  const purchasesColumns = [
    {
      field: "numero_achat",
      headerName: "N° Achat",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "fournisseur_nom",
      headerName: "Fournisseur",
      flex: 1,
    },
    {
      field: "date_achat",
      headerName: "Date",
      flex: 1,
    },
    {
      field: "montant_total",
      headerName: "Montant Total",
      flex: 1,
    },
    {
      field: "type_paiement",
      headerName: "Type Paiement",
      flex: 1,
    },
    {
      field: "statut_paiement",
      headerName: "Statut",
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 150,
      getActions: (params) => {
        const paymentInfo = existingPayments[params.row.id_achat] || {
          exists: false,
          type: null,
        };
        const actions = [];

        // View Details Action
        actions.push(
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="View Details"
            onClick={() => handleViewDetails(params.row)}
            showInMenu={false}
          />
        );

        if (hasPermission("EDIT_SALE")) {
          actions.push(
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => handleEdit(params.row)}
              showInMenu={false}
            />
          );
        }

        if (hasPermission("DELETE_SALE")) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => handleDelete(params.row)}
              showInMenu={false}
            />
          );
        }

        // Payment Action with improved status handling
        actions.push(
          <GridActionsCellItem
            {...getPaymentButtonProps(params.row, paymentInfo)}
          />
        );

        return actions;
      },
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [fournisseursRes, productsRes] = await Promise.all([
        api.get("fournisseur.php"),
        api.get("produits.php"),
      ]);

      setFournisseurs(fournisseursRes.data || []);
      setProducts(productsRes.data || []);

      // Fetch purchases with initial filter (today's date)
      await fetchPurchasesWithFilters();
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPurchaseDetails = async (id_achat) => {
    try {
      const response = await api.get(`details_achats.php?id_achat=${id_achat}`);
      console.log("Fetched Purchase Details:", response.data);
      if (response.data) {
        //console.log("Purchase details:", response.data);
        setPurchaseDetails(response.data);
        fetchData();
      }
    } catch (error) {
      console.error("Error fetching purchase details:", error);
      setError(error.message);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("produits.php");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleViewDetails = (purchase) => {
    console.log("Selected Purchase:", purchase);
    setSelectedPurchase(purchase);
    fetchPurchaseDetails(purchase.id_achat);
    setActiveTab(1);
  };

  const handleEdit = (purchase) => {
    setSelectedPurchase(purchase);
    // setModalEdit(true);
  };

  const handleDelete = (purchase) => {
    setSelectedPurchase(purchase);
    setModalDelete(true);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setSelectedPurchase(null);
      setPurchaseDetails([]);
    }
  };
  if (error) {
    return (
      <Box m="25px">
        <NavigationHeader title="Erreur" subtitle="Une erreur est survenue" />
        <Box sx={{ color: "error.main", mt: 2 }}>{error}</Box>
      </Box>
    );
  }

  const handlePaymentClick = async (purchase) => {
    try {
      // Check for existing payments first
      const paymentStatus = await checkExistingPayments(purchase.id_achat);
      console.log("Payment Status:", paymentStatus);
      if (
        paymentStatus.exists &&
        ((purchase.type_paiement === "Cash" && paymentStatus.type === "cash") ||
          (purchase.type_paiement === "Tranche" &&
            paymentStatus.type === "installment"))
      ) {
        return;
      }

      // Fetch latest purchase data
      const response = await api.get(
        `achats.php?id_achat=${purchase.id_achat}`
      );
      const updatedPurchase = response.data[0];
      console.log("Updated Purchase:", updatedPurchase);

      const detailsResponse = await api.get(
        `details_achats.php?id_achat=${purchase.id_achat}`
      );
      console.log("Details Response:", detailsResponse.data);

      setSelectedPurchase({
        ...updatedPurchase,
        details: detailsResponse.data,
      });

      if (updatedPurchase.type_paiement === "Tranche") {
        setInstallmentModalOpen(true);
        setPaymentModalOpen(false);
      } else {
        setPaymentModalOpen(true);
        setInstallmentModalOpen(false);
      }
    } catch (error) {
      console.error("Error preparing payment:", error);
      setError("Failed to prepare payment modal: " + error.message);
    }
  };

  return (
    <Box m="25px">
      {/* Modals */}
      {selectedPurchase && (
        <>
          <AchatInstallmentPaymentModal
            open={installmentModalOpen}
            handleClose={() => setInstallmentModalOpen(false)}
            purchaseData={selectedPurchase}
            onPaymentComplete={handlePaymentComplete}
          />
          <AchatCashPaymentModal
            open={paymentModalOpen}
            handleClose={() => setPaymentModalOpen(false)}
            purchaseData={selectedPurchase}
            onPaymentComplete={handlePaymentComplete}
          />

          {/* {modalEdit && (
            <EditAchatModal
              open={modalEdit}
              handleClose={() => setModalEdit(false)}
              handleSubmit={handleEditPurchase}
              purchase={selectedPurchase}
              fournisseurs={fournisseurs}
              products={products}
            />
          )} */}

          {modalDelete && (
            <DeleteAchatModal
              open={modalDelete}
              handleClose={() => setModalDelete(false)}
              // handleDelete={() => handleDeletePurchase(selectedPurchase)}
              purchase={selectedPurchase}
            />
          )}
        </>
      )}

      <AddAchatModal
        open={modalAdd}
        handleClose={() => setModalAdd(false)}
        handleSubmit={handleAddPurchase}
        fournisseurs={fournisseurs}
        products={products}
        onProductsUpdated={fetchProducts}
      />

      <NavigationHeader
        title="Gestion des Achats"
        subtitle={
          selectedPurchase
            ? `Achat N° ${selectedPurchase.numero_achat}`
            : "Liste des achats"
        }
      />

      {/* Filter Toggle and Purchases Filter Component */}
      {activeTab === 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowFilter(!showFilter)}
            startIcon={showFilter ? <FilterAltIcon /> : <FilterAltIcon />}
            sx={{
              mb: 1,
              color: colors.secondary,
            }}
          >
            {showFilter ? "Masquer les filtres" : "Afficher les filtres"}
          </Button>
          {showFilter && (
            <AchatFilter
              fournisseurs={fournisseurs}
              onFilterChange={handleFilterChange}
            />
          )}
        </Box>
      )}

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab
          label="Achats"
          style={
            activeTab === 0
              ? {
                  color: colors.secondary,
                }
              : {}
          }
        />
        <Tab
          label="Détails de Achat"
          disabled={!selectedPurchase}
          style={
            activeTab === 1
              ? {
                  color: colors.secondary,
                }
              : {}
          }
        />
        <Tab
          label="Détails de Paiement"
          disabled={!selectedPurchase}
          style={
            activeTab === 2
              ? {
                  color: colors.secondary,
                }
              : {}
          }
        />
      </Tabs>

      <Box display="flex" justifyContent="end" mt="20px">
        {activeTab === 0 && hasPermission("CREATE_SALE") && (
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            onClick={() => setModalAdd(true)}
          >
            Nouvelle Achat
          </Button>
        )}
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.secondary,
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          "& .Mui-disabled": {
            opacity: 0.38,
            pointerEvents: "none",
            cursor: "default",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: `${colors.primary[900]} !important`,
          },
        }}
      >
        {error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            color="error.main"
          >
            {error}
          </Box>
        ) : loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : // Update the DataGrid section in PurchasesManagement.jsx
        activeTab === 0 ? (
          <DataGrid
            rows={purchases}
            columns={purchasesColumns}
            getRowId={(row) => row.id_achat}
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                pageSize: 25,
              },
              sorting: {
                sortModel: [{ field: "date_achat", sort: "desc" }],
              },
            }}
          />
        ) : activeTab === 1 ? (
          <AchatDetailsGrid
            purchaseDetails={purchaseDetails}
            products={products}
            loading={loading}
            colors={colors}
            purchaseId={selectedPurchase?.id_achat}
            onAddDetail={handleAddPurchaseDetail}
            onEditDetail={handleEditPurchaseDetail}
            onDeleteDetail={handleDeletePurchaseDetail}
          />
        ) : (
          <AchatPaymentDetailsGrid
            purchaseId={selectedPurchase?.id_achat}
            paymentType={selectedPurchase?.type_paiement}
            colors={colors}
          />
        )}
      </Box>
    </Box>
  );
}

const CustomToolbar = (props) => {
  return (
    <GridToolbarContainer {...props}>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarExportContainer>
        <GridCsvExportMenuItem options={{ utf8WithBom: true }} />
        <GridPrintExportMenuItem />
      </GridToolbarExportContainer>
      <Box sx={{ marginLeft: "auto" }}>
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
};

export default AchatManagement;
